import React, { ReactNode } from 'react';
import styled from 'styled-components';

import { CARD_WRAPPER_TYPES } from '../../../shared/constants';
import { IMdBody } from '../../../types/shared';

import { ReactMarkdownExtended } from '../../../utils/generic';
import { parseSectionID } from '../../../utils/string-utils';

import mq from '../../../styles/breakpoints';
import { Body } from '../../../styles/generic';
import Spacings from '../../../styles/Spacings';

import check from '../../../images/check_lg.svg';

interface Props {
  cardNumber: number;
  children: ReactNode;
  body?: IMdBody;
  anchorName?: string;
}

const FormWrapper = ({ cardNumber, body, children, anchorName }: Props) => {
  const id = parseSectionID(CARD_WRAPPER_TYPES.formWrapper, anchorName);

  return (
    <section id={id} data-testid={CARD_WRAPPER_TYPES.formWrapper}>
      <Container>
        <FormContainer
          data-testid="form-container"
          isFirstCard={cardNumber === 0}
          hasElevation={!!body}
        >
          {children}
        </FormContainer>
        {body && (
          <StyledBody data-testid="form-body" isFirstCard={cardNumber === 0}>
            <ReactMarkdownExtended>
              {body.childMarkdownRemark.rawMarkdownBody}
            </ReactMarkdownExtended>
          </StyledBody>
        )}
      </Container>
    </section>
  );
};

export default FormWrapper;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: ${Spacings.XL};

  ${mq.from.breakpoint.L`
    flex-direction: row;
    column-gap: ${Spacings.M};
  `}
`;

const FormContainer = styled.div<{
  isFirstCard: boolean;
  hasElevation?: boolean;
}>`
  align-items: center;
  display: flex;
  flex-shrink: 0;
  height: 100%;
  margin: -${Spacings.M} 0 0;

  ${mq.from.breakpoint.M`
    width: 624px;
    box-shadow: ${({
      theme,
      hasElevation,
    }: {
      theme: any;
      hasElevation?: boolean;
    }) => (hasElevation ? theme.elevations.abnElevationProduct : 'none')};
    margin: ${({ isFirstCard }: { isFirstCard: boolean }) =>
      isFirstCard ? '-40px 0 0' : 'unset'};
  `}

  ${mq.from.breakpoint.L`
    margin: ${({ isFirstCard }: { isFirstCard: boolean }) =>
      isFirstCard ? '-80px 0 0' : 'unset'};
  `}
`;

const StyledBody = styled(Body)<{ isFirstCard: boolean }>`
  ${({ isFirstCard }) => !isFirstCard && `margin-top: ${Spacings.XL};`}

  p, ul {
    font-size: 20px;
    line-height: 32px;
  }

  li:before {
    background: url(${check}) 50% 50% no-repeat;
    width: 1.4rem;
  }
`;
